import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import Cropper from 'react-easy-crop';
import { Box, Slider } from '@mui/material';

import { VALID_UPLOAD_IMAGE_TYPES } from 'enrollment/app/constants';
import {
  chooseFamilyDisplayPicture,
  storeFamilyCroppedImage,
} from 'enrollment/features/ProfileEdit/Avatar/AvatarActions';
import { CropContainer } from 'enrollment/features/ProfileEdit/DisplayPicture/DisplayPictureStyled';
import { MAX_DISPLAY_PICTURE_SIZE_TO_LIMIT } from 'enrollment/utils/common/constant';
import { checkForFileSize } from 'enrollment/utils/common/Utils';
import { PsButtonTuner } from 'stencilSetup';
import { EDIT_DEPENDENT_QUERY_KEY } from 'enrollment/queries';
import { ResponseDto, ErrorResponseDto, FormValuesDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';

interface ProfileCropperProps {
  enrollmentContextType?: any;
  handleSaveFamilyPictureDrawer?: any;
  isNewDependent?: boolean;
  dependentId?: any;
  familyDisplayPictureSaved?: boolean;
}

export const ProfileCropper = ({
  enrollmentContextType,
  handleSaveFamilyPictureDrawer,
  dependentId,
  isNewDependent,
  familyDisplayPictureSaved,
}: ProfileCropperProps) => {
  const { t } = useTranslation('pages.enrollmentFamily');
  const dispatch = useDispatch();
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const stateObj = useSelector((state: any) => state?.enrollment?.chooseAvatar);
  const chosenFamilyDisplay = stateObj?.chosenFamilyDisplay;
  const familyPictureObjectURL = stateObj?.familyPictureObjectURL;
  const isFamilyCropped = stateObj?.isFamilyCropped;
  const croppedFamilyImagePath = stateObj?.croppedFamilyImagePath;
  const fileName = stateObj?.familyPictureName;
  const [crop, setCrop] = useState<any>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(0.4);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const displayImage = (event: any) => {
    const selectedFile = event.target.files[0];
    if (checkForFileSize(selectedFile, MAX_DISPLAY_PICTURE_SIZE_TO_LIMIT)) {
      return;
    }
    setZoom(0.4);
    dispatch(chooseFamilyDisplayPicture(selectedFile));
  };

  const saveCroppedImage = (croppedImage: any) => {
    dispatch(storeFamilyCroppedImage(croppedImage));
    const reqObj = {
      dependent_profile: {
        profile_picture: croppedImage,
        file_name: fileName,
        avatar: '',
      },
    };
    isNewDependent
      ? handleSaveFamilyPictureDrawer()
      : saveFamilyDisplayPicture(reqObj);
  };

  // eslint-disable-next-line
  const { isLoading, mutate: saveFamilyDisplayPicture } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    FormValuesDto
  >({
    queryKey: EDIT_DEPENDENT_QUERY_KEY,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/dependents/profile/${dependentId}/${enrollmentContextType}`,
    onSuccess: () => {
      handleSaveFamilyPictureDrawer();
    },
    getErrorMessage: (error) => error.response?.data?.details,
  });

  const onCropChange = (crop: object) => {
    setCrop(crop);
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const onZoomChange = (zoom: any) => {
    setZoom(zoom);
  };

  const getCroppedImg = (imageSrc: any, pixelCrop: any) => {
    const canvas = document.createElement('canvas');
    if (canvas == null) {
      return;
    }
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const circleCtx = canvas.getContext('2d');
    if (circleCtx == null) {
      return;
    }
    circleCtx.fillStyle = 'rgba(0,0,0,0.1)';
    circleCtx.save();
    circleCtx.beginPath();
    circleCtx.arc(
      pixelCrop.width / 2,
      pixelCrop.width / 2,
      pixelCrop.width / 2,
      0,
      Math.PI * 2,
      false
    );
    circleCtx.strokeStyle = '#B7C3E1';
    circleCtx.stroke();
    circleCtx.clip();
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      circleCtx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      circleCtx.restore();
      saveCroppedImage(canvas.toDataURL('image/jpeg'));
    };
  };

  const showCroppedImage = async () => {
    await getCroppedImg(familyPictureObjectURL, croppedAreaPixels);
  };

  const getCropContainer = () => {
    return (
      <>
        <CropContainer>
          <Cropper
            minZoom={0.4}
            image={familyPictureObjectURL}
            crop={crop}
            zoom={zoom}
            cropShape="round"
            showGrid={false}
            aspect={1}
            restrictPosition={false}
            onCropChange={onCropChange}
            onCropComplete={onCropComplete}
            onZoomChange={onZoomChange}
          />
        </CropContainer>
        <Slider
          value={zoom}
          min={0.4}
          max={3}
          step={0.1}
          aria-labelledby="Zoom"
          onChange={(e, zoom: any) => onZoomChange(zoom)}
        />
        <PsButtonTuner
          buttonId="crop-image-button"
          label={t('done')}
          level="tertiary"
          onClick={() => showCroppedImage()}
        />
      </>
    );
  };

  const renderCroppedImage = () => {
    return (
      <>
        <div>
          <Box
            component="img"
            src={croppedFamilyImagePath}
            alt="cropped"
            sx={{ width: '120px', borderRadius: '60px' }}
          ></Box>
        </div>
        <div>
          <label
            htmlFor="family-profile-picture-input"
            className={`btn btn-primary ${isFocused ? 'focus' : ''}`}
          >
            {' '}
            {t('change')}
            <input
              type="file"
              id="family-profile-picture-input"
              accept={VALID_UPLOAD_IMAGE_TYPES}
              onChange={displayImage}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="under-label"
            />
          </label>
        </div>
      </>
    );
  };

  return (
    <div className="text-center">
      <div className="mt-5">
        {chosenFamilyDisplay !== 'picture' && !familyDisplayPictureSaved ? (
          <>
            <div>
              <label
                htmlFor="family-profile-picture-input"
                className={`btn btn-primary ${isFocused ? 'focus' : ''}`}
              >
                {t('uploadPicture')}
                <input
                  type="file"
                  id="family-profile-picture-input"
                  accept={VALID_UPLOAD_IMAGE_TYPES}
                  onChange={displayImage}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="under-label"
                />
              </label>
            </div>
          </>
        ) : isFamilyCropped ? (
          renderCroppedImage()
        ) : (
          getCropContainer()
        )}
      </div>
    </div>
  );
};
